import React from 'react';
import overviewPhone from '../../../assets/images/rabbitX/OVERVIEW_PHONE.webp';
import colors from '../../theme/color';
import { motion } from 'framer-motion';

const OverviewSection = () => {
  return (
    <section className="relative z-20 -mt-4 w-full overflow-hidden pb-20 pt-4 md:-mt-8 md:pb-28 md:pt-8 lg:-mt-10 lg:pb-32 lg:pt-10 2xl:pb-40 min-[2560px]:pb-56">
      <div
        className="pointer-events-none absolute -left-44 top-[10%] hidden h-[750px] w-[500px] rounded-full opacity-[0.08] blur-3xl md:block"
        style={{ backgroundColor: colors.rabbitXAccent }}
      />
      <div
        className="pointer-events-none absolute -right-20 top-[30%] hidden h-[650px] w-[400px] rounded-full opacity-[0.15] blur-3xl lg:block"
        style={{ backgroundColor: colors.rabbitXGlowPurple }}
      />

      <div className="mx-auto w-full max-w-[1380px] px-5 md:px-8 lg:px-12 2xl:max-w-[2000px] 2xl:px-16 min-[2560px]:max-w-[2600px] min-[2560px]:px-24">
        
        {/* Title */}
        <div className="mx-auto mb-12 flex w-full max-w-[1120px] items-center justify-center gap-4 md:mb-16 md:gap-6 2xl:mb-20 2xl:max-w-[1400px] 2xl:gap-8 min-[2560px]:mb-28 min-[2560px]:max-w-[2000px] min-[2560px]:gap-12">
          <span
            className="relative block h-px w-[30px] md:w-[68px] sm:w-[86px]"
            style={{ backgroundColor: `${colors.rabbitXAccent}CC` }}
          >
            <span
              className="absolute -left-[4px] top-1/2 h-[5px] w-[5px] -translate-y-1/2 rounded-full"
              style={{ backgroundColor: `${colors.rabbitXAccent}F2` }}
            />
          </span>

          <span className="font-rajdhani text-[2rem] font-semibold leading-none text-white md:text-[3rem] lg:text-[3.3rem] 2xl:text-[4.2rem] min-[2560px]:text-[6rem]">
            Overview
          </span>

          <span
            className="relative block h-px w-[30px] md:w-[68px] sm:w-[86px]"
            style={{ backgroundColor: `${colors.rabbitXAccent}CC` }}
          >
            <span
              className="absolute -right-[4px] top-1/2 h-[5px] w-[5px] -translate-y-1/2 rounded-full"
              style={{ backgroundColor: `${colors.rabbitXAccent}F2` }}
            />
          </span>
        </div>

        {/* Content */}
        <div className="mx-auto grid w-full max-w-[1240px] items-start gap-8 lg:grid-cols-[auto_minmax(0,1fr)] lg:items-start lg:gap-8 xl:gap-10 2xl:max-w-[1800px] 2xl:gap-12 min-[2560px]:max-w-[2400px] min-[2560px]:gap-14">

          {/* Phone Card */}
          <motion.div className="group relative mx-auto w-full max-w-[700px] shrink-0 rounded-[30px] md:max-w-[780px] lg:mx-0 lg:w-full lg:min-w-[600px] lg:max-w-[980px] 2xl:max-w-[1200px] 2xl:rounded-[40px] min-[2560px]:max-w-[1500px] min-[2560px]:rounded-[52px]">
            
            {/* Glow background on hover */}
            <span
              className="pointer-events-none absolute -inset-3 -z-10 rounded-[34px] opacity-0 blur-2xl transition-all duration-700 ease-out group-hover:opacity-100 group-hover:scale-[1.02] 2xl:rounded-[44px] min-[2560px]:rounded-[56px]"
              style={{
                backgroundImage: `radial-gradient(72% 68% at 50% 40%, ${colors.rabbitXAccent}8A 0%, ${colors.rabbitXGlowPurple}73 44%, transparent 100%)`,
              }}
            />

            {/* 🔥 Gradient Box (UPDATED) */}
            <motion.div
              className="relative w-full h-full overflow-hidden rounded-[30px] px-10 pt-10 pb-0 md:px-12 md:pt-12 md:pb-0 2xl:rounded-[40px] 2xl:px-20 2xl:pt-16 2xl:pb-1 min-[2560px]:rounded-[52px] min-[2560px]:px-28 min-[2560px]:pt-24 min-[2560px]:pb-2"
              style={{
                backgroundImage: `linear-gradient(180deg, ${colors.rabbitXAccent}33 0%, ${colors.rabbitXAccent}00 100%)`,
              }}
              whileHover="hover"
              initial="rest"
              animate="rest"
            >
              {/* Wipe overlay from top to bottom */}
              <motion.div
                className="pointer-events-none absolute inset-0 z-0"
                variants={{
                  rest: { y: '-100%', opacity: 0 },
                  hover: { y: '0%', opacity: 1 },
                }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  backgroundImage: `linear-gradient(180deg, ${colors.rabbitXAccent}66 0%, ${colors.rabbitXAccent}1A 100%)`,
                }}
              />
              {/* Inner glow */}
              <span
                className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[300px] w-[240px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[90px]"
                style={{ backgroundColor: `${colors.rabbitXAccent}33` }}
              />
              <span
                className="pointer-events-none absolute left-1/2 top-1/2 z-0 h-[280px] w-[220px] -translate-x-1/2 -translate-y-[42%] rounded-full blur-[85px]"
                style={{ backgroundColor: `${colors.rabbitXGlowPurple}47` }}
              />

              {/* Phone Image */}
              <motion.img
                src={overviewPhone}
                alt="RabbitX mobile interface"
                className="relative z-10 mx-auto block w-full max-w-[150px] object-contain md:max-w-[250px] lg:w-auto lg:max-w-[300px] 2xl:max-w-[420px] min-[2560px]:max-w-[560px]"
                initial={{ y: 140, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: false, amount: 0.45 }}
                transition={{ duration: 2.8, ease: [0.16, 1, 0.3, 1] }}
              />
            </motion.div>
          </motion.div>

          {/* Text */}
          <motion.div
            className="min-w-0 w-full lg:max-w-none"
            initial={{ x: 90, opacity: 0 }}
            whileInView={{ x: 0, opacity: 1 }}
            viewport={{ once: false, amount: 0.45 }}
            transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1], delay: 0.12 }}
          >
            <p
              className="font-gilroy-medium text-center lg:text-start text-[1rem] leading-[1.55] tracking-normal md:text-[1.38rem] lg:text-[1.8125rem] 2xl:text-[2.15rem] min-[2560px]:text-[3rem]"
              style={{ color: `${colors.rabbitXTextBody}D9` }}
            >
              RabbitX is a modern crypto trading platform designed to deliver a seamless, high-performance
              experience for both beginner &amp; professional traders. It combines real-time market insights,
              advanced charting tools, and intuitive portfolio management into a sleek mobile-first
              interface. The platform emphasizes speed, usability, and transparency, making crypto
              derivatives trading more accessible and engaging.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default OverviewSection;
